import React from 'react';
import { FaStar, FaCheck, FaTimes, FaUsers, FaComments, FaHeart, FaInstagram, FaFacebookF, FaTwitter, FaYoutube, FaApple, FaGooglePlay } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const SuccessStories: React.FC = () => {
  return (
    <div className="bg-gray-50 min-h-screen font-sans" dir="ltr">
      {/* Header Section */}
      <header className="py-12" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Pirooz Success Stories</h1>
            <p className="text-xl md:text-2xl max-w-3xl mb-8 text-white">Discover how Pirooz transformed the Farsi learning journey for students worldwide</p>
            <div className="flex space-x-4">
              <a href="#stories" className="bg-white text-indigo-600 px-6 py-3 rounded-full font-semibold hover:bg-indigo-50 transition">Read Stories</a>
              <Link to="/pathways" className="border-2 border-white px-6 py-3 rounded-full font-semibold text-white hover:bg-white hover:text-indigo-600 transition">Try Pirooz Free</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Success Stories Section */}
      <section id="stories" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Real Students, Real Success</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Story 1 - Premium Feature */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow testimonial-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="flex items-center mb-4">
                <img src="https://randomuser.me/api/portraits/women/32.jpg" alt="Sarah" className="w-16 h-16 rounded-full object-cover mr-4" />
                <div>
                  <h3 className="font-bold text-lg">Sarah Johnson</h3>
                  <div className="text-xs text-white px-2 py-1 rounded-full inline-block" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>Premium User</div>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => <FaStar key={i} />)}
                </div>
                <p className="text-gray-700 italic">"I never thought I could learn Farsi this quickly! The premium features of Pirooz made all the difference."</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-gray-800">"After just 3 months with Pirooz Premium, I could have full conversations with my Iranian friends. The personalized lessons and unlimited practice sessions accelerated my learning beyond what I thought possible. The premium content is worth every penny - it's like having a private tutor in your pocket!"</p>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500">From: London, UK</span>
                <span className="text-sm font-semibold">6 months with </span>
              </div>
            </div>
            {/* Story 2 - Platinum Feature */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow testimonial-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="flex items-center mb-4">
                <img src="https://randomuser.me/api/portraits/men/45.jpg" alt="Michael" className="w-16 h-16 rounded-full object-cover mr-4" />
                <div>
                  <h3 className="font-bold text-lg">Hadi AbdulRashid</h3>
                  <div className="text-xs text-white px-2 py-1 rounded-full inline-block" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>Platinum User</div>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => <FaStar key={i} />)}
                </div>
                <p className="text-gray-700 italic">"Pirooz Platinum connected me with Persians worldwide - and changed my life forever."</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-gray-800">"The platinum membership's community features introduced me to native speakers across the globe. Through the language exchange program, I met Leila, an amazing woman from Tehran. We started as language partners, but our connection grew deeper. Today, we're engaged to be married! Pirooz didn't just teach me Farsi - it helped me find the love of my life."</p>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500">From: Toronto, Canada</span>
                <span className="text-sm font-semibold">1.5 years with Pirooz</span>
              </div>
            </div>
            {/* Story 3 - Premium Feature */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow testimonial-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="flex items-center mb-4">
                <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="Cathrin" className="w-16 h-16 rounded-full object-cover mr-4" />
                <div>
                  <h3 className="font-bold text-lg">Cathrin Miller</h3>
                  <div className="text-xs text-white px-2 py-1 rounded-full inline-block" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>Premium User</div>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => <FaStar key={i} />)}
                </div>
                <p className="text-gray-700 italic">"Pirooz Premium helped me ace my university Farsi course with top marks!"</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-gray-800">"As a Middle Eastern Studies student, I needed to learn Farsi quickly. The premium grammar drills and advanced vocabulary builder helped me master complex concepts that my classmates struggled with. My professor was shocked when I delivered a 15-minute presentation in flawless Farsi just 4 months into the course. I owe my academic success to Pirooz's premium features!"</p>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500">From: Chicago, USA</span>
                <span className="text-sm font-semibold">9 months with Pirooz</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Comparison Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Unlock Your Farsi Potential</h2>
          <p className="text-xl text-center text-gray-600 mb-12">Choose the plan that fits your learning goals</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-center mb-2 text-black">Free</h3>
                <p className="text-black text-center mb-6">Start your Farsi journey</p>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-black">$0</span>
                  <span className="text-black">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Basic vocabulary lessons</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Daily practice exercises</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Limited grammar explanations</span></li>
                  <li className="flex items-center text-gray-400"><FaTimes className="mr-2" /><span>No premium content</span></li>
                  <li className="flex items-center text-gray-400"><FaTimes className="mr-2" /><span>No community access</span></li>
                </ul>
                <Link to="/pathways" className="block w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition text-center">Get Started</Link>
              </div>
            </div>
            {/* Premium Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-yellow-400 relative">
              <div className="absolute top-0 right-0 bg-yellow-400 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">MOST POPULAR</div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-center mb-2 text-black">Premium</h3>
                <p className="text-black text-center mb-6">Accelerate your learning</p>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-black">$9.99</span>
                  <span className="text-black">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>All free features</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Advanced grammar lessons</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Personalized learning path</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Unlimited practice sessions</span></li>
                  <li className="flex items-center text-gray-400"><FaTimes className="mr-2" /><span>Limited community access</span></li>
                </ul>
                <Link to="/subscription" className="block w-full bg-yellow-400 text-white py-3 rounded-lg font-semibold hover:bg-yellow-500 transition text-center">Choose Premium</Link>
              </div>
            </div>
            {/* Platinum Plan */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-center mb-2 text-black">Platinum</h3>
                <p className="text-black text-center mb-6">Immerse yourself completely</p>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-black">$24.99</span>
                  <span className="text-black">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>All premium features</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Full community access</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>1-on-1 tutoring sessions</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Cultural immersion content</span></li>
                  <li className="flex items-center text-black"><FaCheck className="text-green-500 mr-2" /><span>Language exchange program</span></li>
                </ul>
                <Link to="/subscription" className="block w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition text-center">Choose Platinum</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Persian Community Section */}
      <section className="py-16 bg-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-8">
              <h2 className="text-3xl font-bold mb-4 text-white">Join Our Growing Persian Community</h2>
              <p className="text-xl mb-6">Connect with thousands of Persian language learners and native speakers worldwide through Pirooz's exclusive community features.</p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-indigo-700 rounded-full p-2 mr-4"><FaUsers className="text-white" /></div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Language Exchange</h3>
                    <p>Practice with native speakers who want to learn your language</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-indigo-700 rounded-full p-2 mr-4"><FaComments className="text-white" /></div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Cultural Discussions</h3>
                    <p>Engage in meaningful conversations about Persian culture and traditions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-indigo-700 rounded-full p-2 mr-4"><FaHeart className="text-white" /></div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Make Lasting Connections</h3>
                    <p>Build friendships and relationships that go beyond language learning</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-xl p-6 text-gray-800">
                <h3 className="text-2xl font-bold mb-4 text-center">What Our Community Members Say</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <img src="https://randomuser.me/api/portraits/men/22.jpg" alt="David" className="w-12 h-12 rounded-full mr-4" />
                    <div>
                      <p className="font-semibold text-black">David (Platinum Member)</p>
                      <p className="text-sm text-black">"Through Pirooz's community, I found an Iranian mentor who helped me prepare for my business trip to Tehran. The connections I made were invaluable!"</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <img src="https://randomuser.me/api/portraits/women/45.jpg" alt="Fatima" className="w-12 h-12 rounded-full mr-4" />
                    <div>
                      <p className="font-semibold text-black">Fatima (Premium Member)</p>
                      <p className="text-sm text-black">"The cultural discussion groups helped me understand my Iranian husband's background better. Our relationship has improved so much!"</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <img src="https://randomuser.me/api/portraits/men/67.jpg" alt="Ali" className="w-12 h-12 rounded-full mr-4" />
                    <div>
                      <p className="font-semibold text-black">Ali (Native Speaker)</p>
                      <p className="text-sm text-black">"I love helping others learn Farsi while improving my English. Pirooz's community is the most welcoming language platform I've found."</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Elevate Your Farsi Learning?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Join thousands of successful students who achieved fluency with Pirooz's proven method.</p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/register" className="bg-white text-indigo-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-indigo-50 transition">Start 7-Day Free Trial</Link>
            <Link to="/pricing" className="border-2 border-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-indigo-600 transition">Explore Plans</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Pirooz</h3>
              <p className="text-gray-400">The most effective way to learn Farsi online, trusted by thousands of students worldwide.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Learn</h4>
              <ul className="space-y-2">
                <li><Link to="/pathways" className="text-gray-400 hover:text-white transition">Courses</Link></li>
                <li><Link to="/subscription" className="text-gray-400 hover:text-white transition">Pricing</Link></li>
                <li><Link to="/success-stories" className="text-gray-400 hover:text-white transition">Success Stories</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-white transition">Careers</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition">Press</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Connect</h4>
              <div className="flex space-x-4 mb-4">
                <a href="https://instagram.com/vajakapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition"><FaInstagram /></a>
                <a href="https://facebook.com/vajakapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition"><FaFacebookF /></a>
                <a href="https://twitter.com/vajakapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 transition"><FaTwitter /></a>
                <a href="https://youtube.com/vajakapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition"><FaYoutube /></a>
              </div>
              <p className="text-gray-400">Download the app:</p>
              <div className="flex space-x-2 mt-2">
                <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="bg-black text-white px-3 py-2 rounded flex items-center"><FaApple className="mr-2" /><span className="text-xs">Download on the<br /><span className="font-bold">App Store</span></span></a>
                <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="bg-black text-white px-3 py-2 rounded flex items-center"><FaGooglePlay className="mr-2" /><span className="text-xs">Get it on<br /><span className="font-bold">Google Play</span></span></a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">© 2024 پیروز Pirooz. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link to="/terms" className="text-gray-400 hover:text-white transition">Terms</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy</Link>
              <Link to="/cookies" className="text-gray-400 hover:text-white transition">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SuccessStories; 